import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { UserProvider } from './context/UserContext';
import { AudioPlayerProvider } from './context/AudioPlayerContext';
import { QuickDuelProvider } from './context/QuickDuelContext';
import AudioPlayer from './components/audio/AudioPlayer';
import MainPage from './pages/MainPage';
import Menu from './components/Menu';
import ConfirmEmail from "./components/ConfirmEmail";
import CheckUserContext from "./components/CheckUserContext";
import ProfilePage from "./components/ProfilePage";
import Cabinet from "./components/cabinet/Cabinet";
import ProfilesPage from "./components/participants/ProfilesPage";
import HrnbRegisterForm from "./components/auth/HrnbRegisterForm";
import HrnbLoginForm from "./components/auth/HrnbLoginForm";
import BattlesPage from "./pages/BattlesPage";
import BattleDetailPage from "./pages/BattleDetailPage";
import BattleParticipantsPage from "./pages/BattleParticipantsPage";
import CreateBattlePage from "./pages/CreateBattlePage";
import DuelsPage from "./pages/DuelsPage";
import TournamentsPage from "./pages/TournamentsPage";
import QuickDuelsPage from "./pages/QuickDuelsPage";
import QuickDuelDetailPage from "./pages/QuickDuelDetailPage";
import DuelDetailPage from "./pages/DuelDetailPage";
import CreateDuelPage from "./pages/CreateDuelPage";
import TournamentDetailPage from "./pages/TournamentDetailPage";
import AnnualTournamentsPage from "./pages/AnnualTournamentsPage";
import AnnualTournamentDetailPage from "./pages/AnnualTournamentDetailPage";
import ForumPage from "./pages/ForumPage";
import PostDetailPage from "./pages/PostDetailPage";
import TagPage from "./pages/TagPage";
import RulesPage from "./pages/RulesPage";
import RegulationsPage from "./pages/RegulationsPage";
import ChatsLayout from "./components/chats/ChatsLayout";
import ChatsPage from "./pages/ChatsPage";
import ChatWindow from "./components/chats/ChatWindow";
import NewsPage from "./pages/NewsPage";
import ContactsPage from "./pages/ContactsPage";
import NotFoundPage from "./pages/NotFoundPage";
import NotificationsPage from "./pages/NotificationsPage";
import EventsPage from "./pages/EventsPage";
import AudioPage from "./pages/AudioPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VkAuthCallback from "./pages/VkAuthCallback";
import AdminLayout from './admin/components/layout/AdminLayout';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminUsers from './admin/pages/AdminUsers';
import AdminBattles from './admin/pages/AdminBattles';
import AdminBattleParticipants from './admin/pages/AdminBattleParticipants';
import AdminForumCategories from './admin/pages/AdminForumCategories';
import AdminForumPosts from './admin/pages/AdminForumPosts';
import AdminForumComments from './admin/pages/AdminForumComments';
import AdminForumTags from './admin/pages/AdminForumTags';
import AdminRules from './admin/pages/AdminRules';
import AdminPlatformRules from './admin/pages/AdminPlatformRules';
import AdminModeration from './admin/pages/AdminModeration';
import AdminFiles from './admin/pages/AdminFiles';
import AdminChats from './admin/pages/AdminChats';
import AdminStickers from './admin/pages/AdminStickers';
import AdminAuthGuard from './admin/components/layout/AdminAuthGuard';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import WebSocketTestPage from "./pages/WebSocketTestPage";

// Компонент для управления видимостью плеера
function GlobalAudioPlayer() {
    const location = useLocation();

    // Не показываем плеер на странице /audio (там свой плеер)
    if (location.pathname === '/audio') {
        return null;
    }

    return <AudioPlayer />;
}

// Редирект ссылок из писем, которые бэкенд формирует на домене фронтенда
// (/api/auth/confirmation/{token} → /confirmation/{token})
function EmailLinkRedirect({ target }) {
    const params = useParams();
    const token = params.confirmationToken || params.token || '';
    return <Navigate to={`${target}/${encodeURIComponent(token)}`} replace />;
}

function App() {
    return (
        <div className="App">
            <Router>
                <UserProvider>
                    <QuickDuelProvider>
                        <AudioPlayerProvider>
                            <Menu /> {/* Добавляем меню в начало */}
                            <GlobalAudioPlayer /> {/* Глобальный аудиоплеер на всю ширину */}
                            <div className="content-container"> {/* Контейнер для контента */}
                                <Routes>
                        <Route path="/" element={<MainPage />} />
                        <Route path="/login" element={<HrnbLoginForm />} />
                        <Route path="/auth/vk/callback" element={<VkAuthCallback />} />
                        <Route path="/register" element={<HrnbRegisterForm />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                        <Route path="/profiles" element={<ProfilesPage />} />
                        <Route path="/cabinet" element={<Cabinet />} />
                        <Route path="/profile/:id" element={<ProfilePage />} />
                        <Route path="/confirmation/:confirmationToken" element={<ConfirmEmail />} />
                        <Route path="/confirmation" element={<ConfirmEmail />} />

                        {/* Ссылки из писем, которые бэкенд формирует на домене фронтенда */}
                        <Route path="/api/auth/confirmation/:confirmationToken" element={<EmailLinkRedirect target="/confirmation" />} />
                        <Route path="/api/auth/reset-password/:token" element={<EmailLinkRedirect target="/reset-password" />} />

                        <Route path="/check" element={<CheckUserContext />} />
                        <Route path="/ws-test" element={<WebSocketTestPage />} />

                        {/* Battles Routes */}
                        <Route path="/battles" element={<BattlesPage />} />
                        <Route path="/battles/create" element={<CreateBattlePage />} />
                        <Route path="/battles/:id" element={<BattleDetailPage />} />
                        <Route path="/battles/:battleId/participants" element={<BattleParticipantsPage />} />

                        {/* Duels & Tournaments Routes */}
                        <Route path="/duels" element={<DuelsPage />} />
                        <Route path="/duels/create" element={<CreateDuelPage />} />
                        <Route path="/duels/:id" element={<DuelDetailPage />} />
                        <Route path="/tournaments" element={<TournamentsPage />} />
                        <Route path="/tournaments/create" element={<CreateDuelPage />} />
                        <Route path="/tournaments/:id" element={<TournamentDetailPage />} />
                        <Route path="/quick-duels" element={<QuickDuelsPage />} />
                        <Route path="/quick-duels/:id" element={<QuickDuelDetailPage />} />
                        <Route path="/tournaments/annual" element={<AnnualTournamentsPage />} />
                        <Route path="/tournaments/annual/:id" element={<AnnualTournamentDetailPage />} />
                        <Route path="/tournaments/annual/:id/bracket" element={<AnnualTournamentDetailPage />} />

                        {/* Redirects for old URLs */}
                        <Route path="/duels-tournaments" element={<Navigate to="/duels" replace />} />

                        {/* Forum Routes */}
                        <Route path="/forum" element={<ForumPage />} />
                        <Route path="/forum/posts/:id" element={<PostDetailPage />} />
                        <Route path="/forum/tags/:slug" element={<TagPage />} />

                        {/* Rules & Regulations Routes */}
                        <Route path="/rules" element={<RulesPage />} />
                        <Route path="/regulations" element={<RegulationsPage />} />

                        {/* Chats Routes — двухпанельный layout */}
                        <Route path="/chats" element={<ChatsLayout />}>
                            <Route index element={<ChatsPage />} />
                            <Route path=":id" element={<ChatWindow />} />
                        </Route>

                        {/* News & Contacts Routes */}
                        <Route path="/news" element={<NewsPage />} />
                        <Route path="/contacts" element={<ContactsPage />} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/events" element={<EventsPage />} />
                        <Route path="/audio" element={<AudioPage />} />

                        {/* Admin Routes */}
                        <Route path="/admin" element={
                            <AdminAuthGuard requiredAccess="moderator">
                                <AdminLayout />
                            </AdminAuthGuard>
                        }>
                            <Route index element={<AdminDashboard />} />
                            <Route path="dashboard" element={<AdminDashboard />} />

                            {/* Admin-only routes */}
                            <Route path="users" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminUsers />
                                </AdminAuthGuard>
                            } />
                            <Route path="battles" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminBattles />
                                </AdminAuthGuard>
                            } />
                            <Route path="battles/participants" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminBattleParticipants />
                                </AdminAuthGuard>
                            } />
                            <Route path="battles/:battleId/participants" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminBattleParticipants />
                                </AdminAuthGuard>
                            } />
                            <Route path="rules" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminRules />
                                </AdminAuthGuard>
                            } />
                            <Route path="platform-rules" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminPlatformRules />
                                </AdminAuthGuard>
                            } />
                            <Route path="files" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminFiles />
                                </AdminAuthGuard>
                            } />
                            <Route path="chats" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminChats />
                                </AdminAuthGuard>
                            } />
                            <Route path="stickers" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminStickers />
                                </AdminAuthGuard>
                            } />

                            {/* Moderator + Admin routes */}
                            <Route path="moderation" element={
                                <AdminAuthGuard requiredAccess="moderator">
                                    <AdminModeration />
                                </AdminAuthGuard>
                            } />
                            <Route path="forum/categories" element={
                                <AdminAuthGuard requiredAccess="admin">
                                    <AdminForumCategories />
                                </AdminAuthGuard>
                            } />
                            <Route path="forum/posts" element={
                                <AdminAuthGuard requiredAccess="moderator">
                                    <AdminForumPosts />
                                </AdminAuthGuard>
                            } />
                            <Route path="forum/comments" element={
                                <AdminAuthGuard requiredAccess="moderator">
                                    <AdminForumComments />
                                </AdminAuthGuard>
                            } />
                            <Route path="forum/tags" element={
                                <AdminAuthGuard requiredAccess="moderator">
                                    <AdminForumTags />
                                </AdminAuthGuard>
                            } />
                        </Route>

                        {/* 404 Route - must be last */}
                        <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        </div>
                    </AudioPlayerProvider>
                </QuickDuelProvider>
                </UserProvider>
            </Router>
        </div>
    );
}

export default App;